import styled, {
  css,
  DefaultTheme,
  ThemedStyledProps,
} from 'styled-components';
import ButtonBase from 'components/ButtonBase';
import { fadeIn } from 'styles/animations';
import { headlinerGradient } from 'styles/gradient';
import { getShorthandActionColor, getThemeColor } from 'utils/styled';
import { StyledButtonProps } from './types';

const SPECIAL_GRADIENT_ANGLE = 106;

export const createButtonTransition =
  (properties: string[]) => (props: ThemedStyledProps<unknown, DefaultTheme>) =>
    props.theme.transitions.create(['opacity', ...properties]);

export const getButtonThemeColor =
  (variant: 'main' | 'hover') =>
  (
    props: ThemedStyledProps<Pick<StyledButtonProps, '$color'>, DefaultTheme>,
  ) => {
    const { $color } = props;

    if ($color === 'light' || $color === 'dark') {
      return undefined;
    }

    return getShorthandActionColor(
      $color,
      variant === 'main' ? 'main' : 'dark',
    )(props);
  };

const solidButtonStyle = css`
  color: ${getThemeColor('white')};
  background-color: ${getButtonThemeColor('main')};
  transition: ${createButtonTransition(['background-color'])};

  &[data-hovered='true'] {
    background-color: ${getButtonThemeColor('hover')};
  }
`;

const ghostButtonStyle = css`
  background-color: transparent;
  border: 2px solid ${getButtonThemeColor('main')};
  color: ${getButtonThemeColor('main')};
  transition: ${createButtonTransition(['border-color', 'color'])};

  &[data-hovered='true'],
  &[disabled] {
    color: ${getButtonThemeColor('hover')};
    border-color: ${getButtonThemeColor('hover')};
  }
`;

export const specialButtonStyle = css`
  background-image: ${headlinerGradient(SPECIAL_GRADIENT_ANGLE)};
  color: ${getThemeColor('white')};
  z-index: 1;

  &:before {
    background-image: ${headlinerGradient(SPECIAL_GRADIENT_ANGLE + 180)};
    border-radius: inherit;
    content: '';
    height: 100%;
    left: 0;
    opacity: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;
  }

  &[data-hovered='true']:not([disabled]):before {
    animation: ${fadeIn} 1s alternate infinite ease-in-out;
  }
`;

export const frostedButtonStyle = css<Pick<StyledButtonProps, '$color'>>`
  background-color: ${({ $color }) =>
    $color === 'light' ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.25)'};
  backdrop-filter: blur(10px);
  color: ${getThemeColor('white')};
  transition: ${createButtonTransition(['background-color'])};
  &[data-hovered='true'] {
    background-color: ${({ $color }) =>
      $color === 'light' ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.25)'};
  }
`;

export const Button = styled(ButtonBase)<StyledButtonProps>`
  // https://stackoverflow.com/a/29966500
  border-radius: 1000px;
  padding: ${({ $size }) => ($size === 'large' ? '19px 26px' : '9px 15px')};
  width: ${(props) => (props.$fluid ? '100%' : 'auto')};

  ${(props) => {
    switch (props.$variant) {
      case 'solid':
        return solidButtonStyle;

      case 'ghost':
        return ghostButtonStyle;

      case 'frosted':
        return frostedButtonStyle;

      case 'special':
        return specialButtonStyle;

      default:
        return undefined;
    }
  }}
`;

export const ButtonStartIcon = styled.span`
  display: inherit;
  margin-right: 8px;
`;

export const ButtonEndIcon = styled.span`
  display: inherit;
  margin-left: 8px;
`;
